// src/modules/app-views/services/pages/Document/tabs/ParametersTab/modals/BusinessTipModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Flex, Space, Form, Input } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const BusinessTipModal = ({ open, onCancel, onSave, initialData, loading }) => {
  // ---------------------------------------------------------------------------
  // 1. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ---------------------------------------------------------------------------
  // 2. LOCAL VARIABLES
  // ---------------------------------------------------------------------------
  const isEditing = !!initialData;
  const title = isEditing ? translateX('edit_item') : translateX('add_tip');

  // ---------------------------------------------------------------------------
  // 3. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({ text: initialData.text || '' });
      } else {
        form.resetFields();
      }

      // auto-focus on text field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [open, initialData, form]);

  // ---------------------------------------------------------------------------
  // 4. HANDLERS
  // ---------------------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      onSave({ text: (values.text || '').trim() });
    } catch (error) {
      // validation failed - form will show errors
    }
  }, [form, onSave]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const handleAfterClose = useCallback(() => {
    form.resetFields();
  }, [form]);

  // ---------------------------------------------------------------------------
  // 5. UI
  // ---------------------------------------------------------------------------
  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
      destroyOnHidden
      centered
      getContainer={document.body}
      width={500}
      footer={
        <Flex justify="end" style={{ paddingTop: 8 }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
              {translateX('cancel')}
            </Button>
            <Button
              type="primary"
              loading={loading}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
            >
              {translateX('save')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ text: '' }}
      >
        <Form.Item
          name="text"
          label={translateX('tip_text')}
          rules={[{ required: true, message: translateX('required_field') }]}
        >
          <Input.TextArea
            ref={inputRef}
            rows={3}
            maxLength={500}
            showCount
            placeholder={translateX('tip_text')}
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default BusinessTipModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
